import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Graph } from './Graph';
import { Fade, makeStyles, Theme, useMediaQuery, useTheme } from '@material-ui/core';
import { SizeMe } from 'react-sizeme';
import { Button, LoadingData } from '@hwm/ui-lib';
import { useReactToPrint } from 'react-to-print';
import { t } from '@lingui/macro';
import { useDeviceMainGraphDataSource } from './dataSource';
import { useAppSelector } from '@core/store/store';
import { selectedAccStateSelector } from '@core/store/library-stores';
import { ControlCamera, Print } from '@material-ui/icons';
import { GraphExport } from './GraphExport';
import { useFiltersContext } from '@shared/components/Filters/useFiltersContext';
import {
  ActuationsBaseModel,
  BoundaryAndActuationsBaseModel,
  GraphActuationsType,
  GraphBoundariesModel,
  MainGraphFiltersStateModel,
  PMZoneUnitsState,
} from '@shared/models';
import { GraphControls } from './GraphControls';
import { GraphBoundaries } from './GraphBoundaries';
import { GraphSiteInfo } from './GraphSiteInfo';
import { usePmZoneSiteListDataSource } from '@features/dataSource';
import uPlot from 'uplot';
import { GraphLegend } from './GraphLegend';
import { isEmpty } from 'lodash';
import { AppPaths } from '@core/routes/routesPaths';

const useStyles = makeStyles((theme: Theme) => ({
  graph: {
    transition: 'opacity 0.8s ease-in-out',
    minHeight: 232,
  },
}));

export const PRINT_HEIGHT = 744;
export const PRINT_WIDTH = 1400;

export interface ShowStateModel {
  actuations: Partial<Record<GraphActuationsType, ActuationsBaseModel>>;
  bands: GraphBoundariesModel<BoundaryAndActuationsBaseModel>;
  axes: boolean;
  panArrows: boolean;
  empty: boolean;
  channels: boolean[][];
  scales: uPlot.Scales;
}

interface MainGraphProps {
  bandsData: GraphBoundariesModel<number[]>;
  setCurrUnits: React.Dispatch<React.SetStateAction<PMZoneUnitsState>>;
  solenoidActuationThreshold: number;
  /**
   * `dataTestId` - name of a Main Graph (preferably `<name>-main-graph`)
   * @example
   * ```tsx
   * <MainGraph {...props} dataTestId="test-main-graph" />
   */
  dataTestId: string;
}

export const MainGraph: React.FC<MainGraphProps> = ({
  bandsData,
  setCurrUnits,
  solenoidActuationThreshold,
  dataTestId,
}) => {
  const classes = useStyles();

  const theme = useTheme<Theme>();
  const extraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const { pmZoneSites } = usePmZoneSiteListDataSource();

  const plotRef = useRef<uPlot>(null);

  const [showState, setShowState] = useState<ShowStateModel>({
    actuations: null,
    bands: null,
    axes: true,
    panArrows: true,
    empty: true,
    channels: [],
    scales: null,
  });
  const [currFiltersState, setCurrFiltersState] = useState<MainGraphFiltersStateModel>({
    range: null,
    interval: null,
    flowUnit: null,
    pressureUnit: null,
  });

  const { selectedAccount, pmZoneData } = useAppSelector(selectedAccStateSelector);

  const {
    getGraphData,
    graphData: graphDataAPI,
    getActuations,
    actuationsData,
    loading,
  } = useDeviceMainGraphDataSource(selectedAccount?.id);

  const graphData = useMemo(
    () =>
      graphDataAPI
        ?.sort((a) => (a.siteSummary.siteType === 'PRV' ? -1 : 0))
        ?.map((gd) => ({
          ...gd,
          channelData: [
            ...gd.channelData?.sort((a, b) => (a.channelSummary.channelNo < b.channelSummary.channelNo ? -1 : 0)),
          ],
        })) ?? null,
    [graphDataAPI]
  );

  const { graphState, sharedState, setRefreshButton } = useFiltersContext();

  const [graphRendered, setGraphRendered] = useState<boolean>(false);

  const canDisplayActuations = useMemo(
    () =>
      !loading &&
      actuationsData?.[0]?.actuationData &&
      !isEmpty(actuationsData[0].actuationData) &&
      showState.actuations &&
      !isEmpty(showState.actuations),
    [loading, actuationsData, showState.actuations]
  );

  const showRefreshBtn = useMemo(() => {
    return (
      currFiltersState.range?.[0] !== sharedState.range[0] ||
      currFiltersState.range?.[1] !== sharedState.range[1] ||
      currFiltersState.interval !== graphState.interval ||
      currFiltersState.flowUnit !== graphState.flowUnit ||
      currFiltersState.pressureUnit !== graphState.pressureUnit
    );
  }, [
    currFiltersState.range,
    sharedState.range,
    currFiltersState.interval,
    graphState.interval,
    currFiltersState.flowUnit,
    graphState.flowUnit,
    currFiltersState.pressureUnit,
    graphState.pressureUnit,
  ]);

  const graphSitesInfo = useMemo(() => {
    const data = (
      pmZoneSites?.map((site) => {
        const graphSite = graphData?.find((gd) => gd.siteSummary.siteId === site.id);
        return Boolean(graphSite) ? site : null;
      }) ?? []
    ).filter(Boolean);

    return data;
  }, [graphData, pmZoneSites]);

  const getMainGraphData = useCallback(
    (keepState?: boolean) => {
      const params = {
        interval: graphState.interval,
        range: sharedState?.range,
        flowUnit: graphState.flowUnit,
        pressureUnit: graphState.pressureUnit,
      } as MainGraphFiltersStateModel;

      getGraphData(params);
      pmZoneData?.prValveId && getActuations(pmZoneData.prValveId, sharedState.range);

      setCurrFiltersState(params);
      setCurrUnits({ flowUnit: params.flowUnit, pressureUnit: params.pressureUnit });

      setShowState((p) => ({
        actuations: keepState ? p.actuations : null,
        bands: null,
        axes: true,
        panArrows: true,
        empty: true,
        channels: [],
        scales: null,
      }));
    },
    [
      graphState.interval,
      graphState.flowUnit,
      sharedState.range,
      pmZoneData?.prValveId,
      graphState.pressureUnit,
      getGraphData,
      setCurrUnits,
      getActuations,
    ]
  );

  useEffect(() => {
    setRefreshButton({
      show: showRefreshBtn,
      disabled: loading || !graphRendered,
      onClick: () => getMainGraphData(true),
    });
  }, [loading, graphRendered, showRefreshBtn, setRefreshButton, getMainGraphData]);

  useEffect(() => {
    if (
      selectedAccount?.id &&
      pmZoneData?.accountId &&
      currFiltersState.interval === null &&
      sharedState.pageContext === AppPaths.graphsView.path.base
    ) {
      getMainGraphData();
    }
  }, [
    selectedAccount?.id,
    pmZoneData?.accountId,
    sharedState.pageContext,
    currFiltersState.interval,
    getMainGraphData,
  ]);

  useEffect(() => {
    setShowState((p) => ({ ...p, axes: !extraSmallScreen }));
  }, [extraSmallScreen]);

  // PRINT
  const ref = useRef<HTMLDivElement>();
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => ref.current,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
    bodyClass: 'pl-2',
  });

  useEffect(() => {
    if (isPrinting) {
      setTimeout(handlePrint, 44);
    }
  }, [isPrinting, handlePrint]);

  return (
    <div className="flex flex-col w-full h-full overflow-hidden relative">
      <Fade in={graphRendered && !loading && !showState.empty} {...{ timeout: 400 }}>
        <div className="flex flex-row w-full max-w-full mb-1 pr-2">
          <div className="flex flex-col justify-between h-full mr-2">
            <GraphSiteInfo sitesData={graphSitesInfo} dataTestId={`${dataTestId}-site-info`} />
            <GraphBoundaries
              showState={showState}
              setShowState={setShowState}
              canDisplayActuations={canDisplayActuations}
              dataTestId={`${dataTestId}-boundaries`}
            />
          </div>

          {!extraSmallScreen && (
            <div className="ml-auto mr-2">
              <GraphControls plotRef={plotRef} dataTestId={`${dataTestId}-controls`} />
            </div>
          )}

          <div className="flex flex-col justify-between h-full ml-auto sm:ml-0">
            <div className="flex items-center ml-auto">
              {extraSmallScreen && (
                <Button
                  variant="text"
                  style={{
                    height: 28,
                    width: 44,
                    minWidth: 44,
                    marginRight: 8,
                  }}
                  onClick={() => setShowState((p) => ({ ...p, panArrows: !p.panArrows }))}
                  dataTestId={`${dataTestId}-camera-control-button`}
                >
                  <ControlCamera />
                </Button>
              )}
              <GraphExport pmzId={selectedAccount?.id ?? null} />
              <Button
                style={{
                  height: 28,
                  width: 44,
                  minWidth: 44,
                  marginLeft: 8,
                }}
                onClick={() => setIsPrinting(true)}
                disabled={!graphRendered}
                dataTestId={`${dataTestId}-print-button`}
              >
                <Print />
              </Button>
            </div>

            {extraSmallScreen && (
              <Button
                variant="text"
                style={{
                  display: 'flex',
                  alignSelf: 'center',
                  height: 28,
                  width: 80,
                  minWidth: 80,
                  marginTop: 4,
                }}
                onClick={() => setShowState((p) => ({ ...p, axes: !p.axes }))}
                dataTestId={`${dataTestId}-toggle-axes-button`}
              >{`${showState.axes ? t`Hide` : t`Show`} ${t`Axes`}`}</Button>
            )}
          </div>
        </div>
      </Fade>

      {loading || !graphRendered ? (
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2" style={{ zIndex: 9999 }}>
          <LoadingData dataTestId={`${dataTestId}-loading`} />
        </div>
      ) : null}

      <SizeMe monitorHeight monitorWidth refreshRate={500} refreshMode="debounce">
        {({ size }) => {
          return (
            <div className="flex flex-col w-full h-full overflow-x-hidden overflow-y-auto">
              <div ref={ref} className={classes.graph} style={{ opacity: !(loading || !graphRendered) ? 1 : 0 }}>
                <div
                  style={
                    isPrinting
                      ? {
                          width: PRINT_WIDTH,
                          minWidth: PRINT_WIDTH,
                          pointerEvents: 'none',
                        }
                      : {}
                  }
                >
                  <Graph
                    bandsData={bandsData}
                    size={{
                      height: isPrinting ? PRINT_HEIGHT : size?.height ?? 200,
                      width: isPrinting ? PRINT_WIDTH : (size?.width ?? 200) - 16,
                    }}
                    sitesData={graphSitesInfo}
                    isPrinting={isPrinting}
                    graphData={graphData}
                    showState={showState}
                    setShowState={setShowState}
                    actuationsData={actuationsData}
                    solenoidActuationThreshold={solenoidActuationThreshold}
                    isGraphLoading={loading}
                    plotRef={plotRef}
                    setPlotRef={(ref: uPlot) => (plotRef.current = ref)}
                    graphRendered={graphRendered}
                    setGraphRendered={setGraphRendered}
                    currFiltersState={currFiltersState}
                    dataTestId={dataTestId}
                  />
                  {graphRendered && !showState.empty && (
                    <GraphLegend
                      showState={showState}
                      setShowState={setShowState}
                      graphData={graphData}
                      sitesData={graphSitesInfo}
                      isPrinting={isPrinting}
                      solenoidActuationThreshold={solenoidActuationThreshold}
                      dataTestId={`${dataTestId}-legend`}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        }}
      </SizeMe>
    </div>
  );
};
